// InspirationSearch.jsx - Version complète avec ObjectifEditor intégré
import React, { useState, useEffect, useRef } from 'react';
import { 
  CircularProgress, 
  Alert, 
  Button,
  Chip,
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Tooltip
} from '@mui/material';
import { 
  Search, 
  Globe, 
  Settings, 
  Languages, 
  MapPin,
  User,
  Star,
  HelpCircle,
  Eye,
  BookOpen,
  Target,
  Edit
} from 'lucide-react';

// Import du module géographique commun
import GeoLanguageService from '../components/GeoLanguageService';

// Import de ObjectifEditor pour l'édition de l'objectif
import ObjectifEditor from '../CP-Inspiration/ObjectifEditor';

const InspirationSearch = ({ 
  searchParams, 
  setSearchParams,
  onSearch,
  loading,
  error,
  currentCompetence,
  token,
  userPreferences = null,
  geographicAnalysis = null,
  preferencesLoaded = false,
  autoConfigApplied = null,
  onApplyGeographicConfig = null,
  // Props pour la navigation
  hasExistingSources = false,
  hasExistingSynthesis = false,
  onNavigateToResults = null,
  onNavigateToSynthesis = null,
  // Nouvelle prop pour les métadonnées
  metadata = {},
  // Nouvelle prop pour la mise à jour de l'objectif
  onObjectiveUpdate = null
}) => {
  // ============================================================================
  // ÉTATS LOCAUX SIMPLIFIÉS
  // ============================================================================
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [conversationData, setConversationData] = useState({
    objectiveHistory: []
  });

  // Ref pour éviter les sauvegardes multiples
  const lastSavedHistoryRef = useRef(null);

  // ============================================================================
  // CHARGEMENT DE L'HISTORIQUE DEPUIS FIREBASE
  // ============================================================================
  useEffect(() => {
    const loadObjectiveHistory = async () => {
      if (!currentCompetence?.id || !token) return;

      try {
        const { getFirestore, doc, getDoc } = await import('firebase/firestore');
        const { getAuth } = await import('firebase/auth');
        const db = getFirestore();
        const auth = getAuth();
        const userId = auth.currentUser?.uid;

        if (userId) {
          const competenceRef = doc(db, 'users', userId, 'competences', currentCompetence.id);
          const competenceDoc = await getDoc(competenceRef);

          if (competenceDoc.exists()) {
            const data = competenceDoc.data();
            console.log('🔍 DEBUG - conversationData depuis Firebase:', data.conversationData);
            if (data.conversationData?.objectiveHistory) {
              console.log('🔍 DEBUG - objectiveHistory brut:', data.conversationData.objectiveHistory);
              setConversationData(prev => ({
                ...prev,
                objectiveHistory: data.conversationData.objectiveHistory
              }));
              console.log('✅ Historique des objectifs chargé:', data.conversationData.objectiveHistory.length, 'entrées');
            } else {
              console.log('ℹ️ Aucun historique trouvé dans Firebase');
            }
          }
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement de l\'historique:', error);
      }
    };

    loadObjectiveHistory();
  }, [currentCompetence?.id, token]);

  // ============================================================================
  // CONFIGURATION DES OPTIONS
  // ============================================================================
  const availableProviders = [
    { value: 'Google', label: 'Google', icon: '🌐' },
    { value: 'Perplexity', label: 'Perplexity', icon: '🔍' },
    { value: 'Aladinn', label: 'Aladinn', icon: '🚀' }
  ];

  const availableLanguages = GeoLanguageService.AVAILABLE_LANGUAGES;
  const availableCountries = GeoLanguageService.AVAILABLE_COUNTRIES;

  // ============================================================================
  // FONCTIONS UTILITAIRES
  // ============================================================================
  
  /**
   * Détermine le type de langue pour l'affichage
   */
  const getLanguageDisplayType = (langCode, countryCode) => {
    // Priorité 1 : Langue de l'utilisateur
    if (userPreferences && langCode === userPreferences.language) {
      return 'user';
    }
    
    // Priorité 2 : Langue recommandée par l'analyse géographique
    if (geographicAnalysis?.hasGeographicContext && countryCode) {
      const countryLanguageMap = {
        'FR': ['fr'],
        'CA': ['fr', 'en'],
        'MA': ['fr', 'ar'],
        'ES': ['es'],
        'DE': ['de'],
        'IT': ['it'],
        'PT': ['pt']
      };
      
      const recommendedLangs = countryLanguageMap[countryCode] || [];
      if (recommendedLangs.includes(langCode)) {
        return 'recommended';
      }
    }
    
    // Priorité 3 : Anglais international
    if (langCode === 'en') {
      return 'international';
    }
    
    // Par défaut : sélectionnée
    return 'selected';
  };

  /**
   * Obtient la couleur et l'icône selon le type de langue
   */
  const getLanguageStyle = (type) => {
    switch (type) {
      case 'user':
        return {
          color: 'info',
          variant: 'filled',
          icon: <User className="h-3 w-3" />,
          chipStyle: { backgroundColor: '#1976d2', color: 'white', fontWeight: 600 },
          label: 'Votre langue'
        };
      case 'recommended':
        return {
          color: 'success',
          variant: 'filled',
          icon: <Star className="h-3 w-3" />,
          chipStyle: { backgroundColor: '#2e7d32', color: 'white', fontWeight: 600 },
          label: 'Recommandée par géolocalisation'
        };
      case 'international':
        return {
          color: 'default',
          variant: 'outlined',
          icon: <Globe className="h-3 w-3" />,
          chipStyle: { backgroundColor: '#f3e5f5', color: '#7b1fa2', fontWeight: 400 },
          label: 'Internationale'
        };
      case 'selected':
        return {
          color: 'default',
          variant: 'outlined',
          icon: <User className="h-3 w-3" />,
          chipStyle: { backgroundColor: '#fff3e0', color: '#f57c00', fontWeight: 400 },
          label: 'Sélectionnée'
        };
      default:
        return {
          color: 'default',
          variant: 'outlined',
          icon: null,
          chipStyle: { backgroundColor: '#f5f5f5', color: '#616161' },
          label: 'Autre'
        };
    }
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleProviderChange = (provider) => {
    setSearchParams(prev => ({
      ...prev,
      selectedProviders: prev.selectedProviders.includes(provider)
        ? prev.selectedProviders.filter(p => p !== provider)
        : [...prev.selectedProviders, provider]
    }));
  };

  const handleLanguageChange = (event) => {
    const {
      target: { value },
    } = event;
    const selectedLanguages = typeof value === 'string' ? value.split(',') : value;
    setSearchParams(prev => ({
      ...prev,
      languages: selectedLanguages
    }));
  };

  const handleCountryChange = (newCountry) => {
    setSearchParams(prev => ({ ...prev, country: newCountry }));
    
    if (userPreferences && GeoLanguageService.getBackendSupportedLanguages) {
      const countryLanguages = GeoLanguageService.getBackendSupportedLanguages(newCountry);
      const userLang = userPreferences.language || 'fr';
      const recommendedLanguages = [userLang, ...countryLanguages, 'en']
        .filter((lang, index, arr) => arr.indexOf(lang) === index)
        .slice(0, 4);
      
      setSearchParams(prev => ({ 
        ...prev, 
        country: newCountry,
        languages: recommendedLanguages 
      }));
    }
  };

  const handleSearch = () => {
    onSearch({
      ...searchParams,
      domains: searchParams.country ? GeoLanguageService.getCountryDomains(searchParams.country) : []
    });
  };

  // ============================================================================
  // COMPOSANTS - NOUVEL ORDRE D'AFFICHAGE
  // ============================================================================
  
  // 2. Message de succès (résultat immédiat)
  const SuccessMessage = () => {
    if (!metadata.count || metadata.count === 0) return null;

    return (
      <Alert severity="success" className="mb-6">
        ✅ {metadata.count} sources d'inspiration 
        {hasExistingSynthesis && ' • Synthèse'}
      </Alert>
    );
  };

  // 3. Boutons de navigation
  const NavigationButtons = () => {
    if (!hasExistingSources && !hasExistingSynthesis) return null;

    return (
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-6">
        {hasExistingSources && onNavigateToResults && (
          <Button
            onClick={onNavigateToResults}
            variant="outlined"
            startIcon={<Eye />}
            className="text-blue-600 border-blue-300 hover:bg-blue-50 w-full sm:w-auto"
            sx={{ textTransform: 'none' }}
          >
            <span className="sm:hidden">Voir les sources</span>
            <span className="hidden sm:inline">Consulter les sources</span>
          </Button>
        )}
        {hasExistingSynthesis && onNavigateToSynthesis && (
          <Button
            onClick={onNavigateToSynthesis}
            variant="outlined"
            startIcon={<BookOpen />}
            className="text-green-600 border-green-300 hover:bg-green-50 w-full sm:w-auto"
            sx={{ textTransform: 'none' }}
          >
            <span className="sm:hidden">Voir la synthèse</span>
            <span className="hidden sm:inline">Consulter la synthèse</span>
          </Button>
        )}
      </div>
    );
  };

  // 1. Affichage de l'objectif avec ObjectifEditor - Version simplifiée sans redondance
  const ObjectiveDisplay = () => {
    if (!searchParams.objective && !currentCompetence?.title) return null;

    // Handler pour la mise à jour de l'objectif
    const handleObjectiveSave = async (newObjective) => {
      try {
        // ✅ IMPORTANT: Si onObjectiveUpdate est fourni, déléguer au parent
        // Le parent (InspirationModule) gérera la mise à jour ET la détection géographique
        if (onObjectiveUpdate) {
          await onObjectiveUpdate(newObjective);
          console.log('✅ Mise à jour déléguée au parent InspirationModule (détection géographique incluse)');
        } else {
          // Fallback : Mettre à jour les paramètres de recherche localement si pas de callback parent
          setSearchParams(prev => ({
            ...prev,
            objective: newObjective
          }));
          console.log('✅ Objectif de recherche mis à jour localement (pas de callback parent)');
        }

        console.log('✅ Objectif mis à jour:', newObjective);
      } catch (error) {
        console.error('❌ Erreur lors de la mise à jour de l\'objectif:', error);
        throw error;
      }
    };

    // Handler pour la mise à jour des données de conversation (historique)
    const handleConversationUpdate = async (updates) => {
      // Mettre à jour l'état local
      setConversationData(prev => ({
        ...prev,
        ...updates
      }));

      // ✅ Sauvegarder l'historique dans Firebase si disponible
      if (currentCompetence?.id && token && updates.objectiveHistory) {
        // Guard: éviter les sauvegardes multiples avec le même historique
        const historyString = JSON.stringify(updates.objectiveHistory);
        if (historyString === lastSavedHistoryRef.current) {
          console.log('⏭️ Historique identique - pas de sauvegarde Firebase');
          return;
        }

        try {
          const { getFirestore, doc, updateDoc } = await import('firebase/firestore');
          const { getAuth } = await import('firebase/auth');
          const db = getFirestore();
          const auth = getAuth();
          const userId = auth.currentUser?.uid;

          if (userId) {
            const competenceRef = doc(db, 'users', userId, 'competences', currentCompetence.id);

            await updateDoc(competenceRef, {
              'conversationData.objectiveHistory': updates.objectiveHistory,
              updatedAt: new Date().toISOString()
            });

            // Mémoriser l'historique sauvegardé
            lastSavedHistoryRef.current = historyString;
            console.log('✅ Historique sauvegardé dans Firebase:', updates.objectiveHistory.length, 'entrées');
          }
        } catch (error) {
          console.error('❌ Erreur lors de la sauvegarde de l\'historique:', error);
        }
      }
    };

    return (
      <div className="mb-6">
        {/* ObjectifEditor affiché directement sans double fond */}
        <ObjectifEditor
          objectif={searchParams.objective || currentCompetence?.title}
          onSave={handleObjectiveSave}
          competenceId={currentCompetence?.id}
          isReadOnly={false}
          conversationData={conversationData}
          onConversationUpdate={handleConversationUpdate}
        />
      </div>
    );
  };

  // 5. Analyse géographique automatique
  const GeographicAnalysisDisplay = () => {
    if (!geographicAnalysis || !geographicAnalysis.hasGeographicContext) {
      return null;
    }

    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="text-blue-800 font-semibold mb-3 flex items-center">
          <MapPin className="h-5 w-5 mr-2" />
          Analyse géographique automatique
          <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
            Détectée
          </span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-blue-700 text-sm font-medium mb-2">Pays détectés :</p>
            <div className="flex flex-wrap gap-1">
              {geographicAnalysis.countries.map((country, index) => (
                <span 
                  key={index} 
                  className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium"
                >
                  {country.name}
                </span>
              ))}
            </div>
          </div>

          <div>
            <p className="text-green-700 text-sm font-medium mb-2">Configuration appliquée :</p>
            <div className="text-sm text-green-600">
              <div>Pays : {searchParams.country ? GeoLanguageService.getCountryInfo(searchParams.country)?.name : 'Mondial'}</div>
              <div>Langues : {searchParams.languages.map(lang => 
                GeoLanguageService.getLanguageInfo(lang)?.name || lang
              ).join(', ')}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 6. Sources de recherche (providers)
  const ProviderSelector = () => (
    <div className="mb-6">
      <label className="block text-gray-700 mb-2 font-medium flex items-center">
        <Globe className="h-4 w-4 mr-2" />
        Sources de recherche
      </label>
      <div className="flex flex-wrap gap-2">
        {availableProviders.map(provider => (
          <button
            key={provider.value}
            type="button"
            onClick={() => handleProviderChange(provider.value)}
            className={`flex items-center px-3 py-2 rounded-lg border transition-colors ${
              searchParams.selectedProviders.includes(provider.value)
                ? 'bg-purple-100 border-purple-300 text-purple-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <span className="mr-2">{provider.icon}</span>
            {provider.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Google et Perplexity sont recommandés pour trouver des sources d'inspiration variées
      </p>
    </div>
  );

  const UserPreferencesIndicator = () => {
    if (!preferencesLoaded) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <div className="flex items-center text-yellow-700">
            <CircularProgress size={16} className="mr-2" />
            <span className="text-sm">Chargement de vos préférences...</span>
          </div>
        </div>
      );
    }

    if (!userPreferences) {
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
          <div className="flex items-center text-gray-600">
            <User className="h-4 w-4 mr-2" />
            <span className="text-sm">Configuration par défaut utilisée</span>
          </div>
        </div>
      );
    }
  };

  const AdvancedOptions = () => {
    if (!showAdvanced) return null;

    return (
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
      {/* 6. Sources de recherche (providers) */}
      <ProviderSelector />

        {/* Pays */}
        <div>
          <label className="block text-gray-700 mb-2 font-medium flex items-center">
            <MapPin className="h-4 w-4 mr-2" />
            Région
            {geographicAnalysis?.hasGeographicContext && (
              <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                Auto-détectée
              </span>
            )}
          </label>
          <select
            value={searchParams.country}
            onChange={(e) => handleCountryChange(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            {availableCountries.map(country => (
              <option key={country.code} value={country.code}>
                {country.name}
                {userPreferences?.country === country.code ? ' (Votre région)' : ''}
                {geographicAnalysis?.countries?.some(c => c.code === country.code) ? ' (Détectée)' : ''}
                {country.region && country.region !== 'Global' ? ` (${country.region})` : ''}
              </option>
            ))}
          </select>
          {searchParams.country && GeoLanguageService.getCountryDomains && (
            <p className="text-xs text-blue-600 mt-1">
              Domaines locaux : {GeoLanguageService.getCountryDomains(searchParams.country).join(', ')}
            </p>
          )}
        </div>

        {/* Sélecteur de langues avec affichage amélioré */}
        <div>
          <label className="block text-gray-700 mb-2 font-medium flex items-center">
            <Languages className="h-4 w-4 mr-2" />
            Langues des résultats
            {geographicAnalysis?.hasGeographicContext && (
              <Tooltip title="Langues automatiquement configurées selon votre localisation">
                <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded cursor-help">
                  Geo-optimisées
                </span>
              </Tooltip>
            )}
          </label>

          <FormControl fullWidth>
            <Select
              multiple
              value={searchParams.languages}
              onChange={handleLanguageChange}
              input={<OutlinedInput className="bg-white" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((langCode) => {
                    const lang = GeoLanguageService.getLanguageInfo(langCode);
                    const displayType = getLanguageDisplayType(langCode, searchParams.country);
                    const style = getLanguageStyle(displayType);
                    
                    return (
                      <Chip 
                        key={langCode} 
                        label={
                          <span className="flex items-center">
                            {style.icon && <span className="mr-1">{style.icon}</span>}
                            {lang ? lang.name : langCode.toUpperCase()}
                          </span>
                        }
                        size="small"
                        sx={style.chipStyle}
                      />
                    );
                  })}
                </Box>
              )}
              disabled={loading}
              className="w-full border border-gray-300 rounded-lg"
            >
              {availableLanguages.map((lang) => {
                const displayType = getLanguageDisplayType(lang.code, searchParams.country);
                const style = getLanguageStyle(displayType);
                const isSelected = searchParams.languages.indexOf(lang.code) > -1;
                
                return (
                  <MenuItem key={lang.code} value={lang.code}>
                    <Checkbox checked={isSelected} />
                    <ListItemText 
                      primary={
                        <span className="flex items-center">
                          {style.icon && <span className="mr-2">{style.icon}</span>}
                          {lang.name}
                          {userPreferences?.language === lang.code && (
                            <span className="ml-2 px-1 bg-blue-100 text-blue-700 text-xs rounded">Vôtre</span>
                          )}
                          {geographicAnalysis?.countries?.some(c => c.code === searchParams.country) && 
                           displayType === 'recommended' && (
                            <span className="ml-2 px-1 bg-green-100 text-green-700 text-xs rounded">Géo</span>
                          )}
                        </span>
                      }
                      secondary={`Code: ${lang.code.toUpperCase()} • ${style.label}`}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          
          <p className="text-xs text-gray-500 mt-2">
            ✅ Les langues sont automatiquement optimisées selon votre géolocalisation et vos préférences.
          </p>
        </div>

        {/* Nombre de sources */}
        <div>
          <label className="block text-gray-700 mb-2 font-medium">
            Nombre de sources à explorer
          </label>
          <select
            value={searchParams.maxSources}
            onChange={(e) => setSearchParams(prev => ({ ...prev, maxSources: Number(e.target.value) }))}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <option value={15}>15 sources (recommandé)</option>
            <option value={12}>12 sources</option>
            <option value={8}>8 sources</option>
            <option value={5}>5 sources</option>
          </select>
        </div>

      </div>
    );
  };

  const SearchButton = () => (
    <div className="flex justify-center">
      <button
        type="button"
        onClick={handleSearch}
        disabled={loading || !searchParams.objective.trim() || searchParams.selectedProviders.length === 0 || !token}
        className="flex items-center justify-center bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
      >
        {loading ? (
          <>
            <CircularProgress size={20} className="mr-2 text-white" />
            <span className="sm:hidden">Recherche...</span>
            <span className="hidden sm:inline">Recherche en cours...</span>
          </>
        ) : !token ? (
          <>
            <CircularProgress size={20} className="mr-2 text-white" />
            Connexion...
          </>
        ) : (
          <>
            <Search className="h-5 w-5 mr-2" />
            <span className="sm:hidden">Chercher</span>
            <span className="hidden sm:inline">Chercher l'inspiration</span>
          </>
        )}
      </button>
    </div>
  );

  const ConfigurationSummary = () => {
    if (!searchParams.languages.length && !searchParams.country) return null;

    return (
      <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
        <div className="text-sm">
          <div className="font-medium text-green-800 mb-2 flex items-center">
            <span className="mr-2">🎯</span>
            Configuration active
            {preferencesLoaded && userPreferences && (
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                Personnalisée
              </span>
            )}
            {geographicAnalysis?.hasGeographicContext && (
              <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                Géo-optimisée
              </span>
            )}
          </div>
          <div className="space-y-1 text-green-700">
            <div>
              <strong>Langues :</strong> 
              {searchParams.languages.map((langCode, index) => {
                const lang = GeoLanguageService.getLanguageInfo(langCode);
                const displayType = getLanguageDisplayType(langCode, searchParams.country);
                const getIcon = (type) => {
                  switch(type) {
                    case 'user': return '👤';
                    case 'recommended': return '⭐';
                    case 'international': return '🌐';
                    case 'selected': return '🔸';
                    default: return '';
                  }
                };
                const icon = getIcon(displayType);
                return (
                  <span key={langCode} className="ml-1">
                    {index > 0 ? ', ' : ''}
                    {icon} {lang ? lang.name : langCode.toUpperCase()}
                  </span>
                );
              })}
            </div>
            <div>
              <strong>Région :</strong> {searchParams.country ? GeoLanguageService.getCountryInfo(searchParams.country)?.name : 'Mondiale'}
              {userPreferences?.country === searchParams.country && (
                <span className="ml-1 text-blue-600">👤</span>
              )}
              {geographicAnalysis?.countries?.some(c => c.code === searchParams.country) && (
                <span className="ml-1 text-green-600">📍</span>
              )}
            </div>
            <div>
              <strong>Sources :</strong> {searchParams.selectedProviders.join(', ')} • Max {searchParams.maxSources} résultats
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // RENDU PRINCIPAL - NOUVEL ORDRE D'AFFICHAGE
  // ============================================================================
  return (
    <div className="space-y-6">

      {/* 1. Affichage de l'objectif actif - HARMONISÉ */}
      <ObjectiveDisplay />

      {/* 2. Message de succès "10 sources d'inspiration trouvées" */}
      <SuccessMessage />

      {/* 3. Boutons de navigation "Consulter les sources" / "Consulter la synthèse" */}
      <NavigationButtons />

      {/* Indicateur des préférences utilisateur */}
      <UserPreferencesIndicator />

      {/* 5. Analyse géographique automatique */}
      <GeographicAnalysisDisplay />

      {/* Options avancées */}
      <div>
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center text-purple-600 hover:text-purple-800 font-medium"
        >
          <Settings className="h-5 w-5 mr-2" />
          {showAdvanced ? "Masquer les options" : "Options avancées"}
        </button>
      </div>

      <AdvancedOptions />

      {/* Bouton de recherche */}
      <SearchButton />

      {/* Alertes de validation */}
      {searchParams.selectedProviders.length === 0 && (
        <Alert severity="warning">
          Veuillez sélectionner au moins une source de recherche pour continuer.
        </Alert>
      )}

      {!token && (
        <Alert severity="info">
          Connexion en cours... Veuillez patienter.
        </Alert>
      )}

      {error && (
        <Alert severity="error">
          {error}
        </Alert>
      )}

      {/* Résumé de la configuration */}
      <ConfigurationSummary />
    </div>
  ); 
};

export default InspirationSearch;
